import React from 'react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto bg-white p-8 rounded-lg shadow">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Terms of Service</h1>
        <p className="text-sm text-gray-500 mb-8">Last updated: 12/29/24</p>

        <div className="space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-600">By accessing or using NumisNavigator, you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not access the service.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Account Terms</h2>
            <ul className="list-disc pl-5 text-gray-600">
              <li>You must provide accurate and complete information when creating an account</li>
              <li>You are responsible for maintaining the security of your account credentials</li>
              <li>You must notify us immediately of any unauthorized access to your account</li>
              <li>We reserve the right to suspend or terminate accounts that violate these terms</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Service Usage</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">3.1 Acceptable Use</h3>
                <p className="text-gray-600 mb-2">You agree to use the service only for:</p>
                <ul className="list-disc pl-5 text-gray-600">
                  <li>Managing your coin collection inventory</li>
                  <li>Processing certification submissions</li>
                  <li>Tracking transactions and client relationships</li>
                  <li>Other legitimate numismatic business purposes</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">3.2 Prohibited Activities</h3>
                <p className="text-gray-600 mb-2">You may not:</p>
                <ul className="list-disc pl-5 text-gray-600">
                  <li>Use the service for any illegal purposes</li>
                  <li>Upload malicious content or attempt to compromise system security</li>
                  <li>Impersonate others or provide false information</li>
                  <li>Attempt to reverse engineer the service</li>
                  <li>Resell or redistribute the service without authorization</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Content and Data</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">4.1 Your Content</h3>
                <p className="text-gray-600">You retain ownership of your content (coin images, descriptions, etc.) but grant us necessary rights to provide the service.</p>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">4.2 Data Accuracy</h3>
                <p className="text-gray-600">While we strive for accuracy, we cannot guarantee the accuracy of certification data or third-party information.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Third-Party Services</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">5.1 General Integration</h3>
                <p className="text-gray-600">Our service integrates with third-party certification services (NGC, PCGS). Use of these services is subject to their respective terms and conditions.</p>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">5.2 PCGS API Usage</h3>
                <p className="text-gray-600 mb-2">When using PCGS API integration features:</p>
                <ul className="list-disc pl-5 text-gray-600">
                  <li>You are responsible for obtaining and maintaining a valid PCGS API key</li>
                  <li>You must comply with all PCGS API terms of service and usage guidelines</li>
                  <li>You agree not to share, distribute, or expose your PCGS API key</li>
                  <li>You are responsible for any fees or charges associated with your PCGS API usage</li>
                  <li>You acknowledge that misuse of the PCGS API may result in termination of your API access</li>
                  <li>We are not responsible for any changes, interruptions, or discontinuation of the PCGS API service</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">5.3 API Key Security</h3>
                <p className="text-gray-600">You are responsible for maintaining the security and confidentiality of your API keys. Any activities that occur using your API keys are your responsibility. You must notify us immediately of any unauthorized use or security breach of your API keys.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Service Availability and Updates</h2>
            <ul className="list-disc pl-5 text-gray-600">
              <li>We strive to maintain service availability but do not guarantee uninterrupted access</li>
              <li>We may modify or discontinue features with reasonable notice</li>
              <li>We will provide security updates and bug fixes as needed</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Fees and Payment</h2>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Subscription fees are billed according to your selected plan</li>
              <li>Certification submission fees are separate and subject to third-party pricing</li>
              <li>Refunds are handled according to our refund policy</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-gray-600 mb-4">We are not liable for:</p>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Loss or damage to your coin collection data</li>
              <li>Accuracy of third-party certification results</li>
              <li>Business losses or indirect damages</li>
              <li>Service interruptions or technical issues</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Indemnification</h2>
            <p className="text-gray-600">You agree to indemnify and hold us harmless from any claims arising from your use of the service or violation of these terms.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Changes to Terms</h2>
            <p className="text-gray-600">We may modify these terms at any time. Continued use of the service after changes constitutes acceptance of the new terms.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Governing Law</h2>
            <p className="text-gray-600">These terms are governed by Maryland law. Any disputes will be resolved in the courts of Maryland.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Contact</h2>
            <p className="text-gray-600">For questions about these terms, please contact us at hello@numisnavigator.com.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
